/**
 * The authorization vocabulary — arch §4.3 layer 3.
 *
 * "Modules ask 'may this principal perform this action on this resource?' and never implement
 * their own checks." This package holds the question, the answer, and the preset roles. It holds
 * no decider: `access` implements the decision, and one day a policy service may implement it
 * instead. A module asking the question depends on this vocabulary and on nothing else, which is
 * what stops the decision from being re-derived in eight places with eight different bugs.
 *
 * Deliberately data, not logic. The matrix below is a fact about the product, and facts belong
 * somewhere both the API and the recruiter app can read (FE-02 gates routes on the same
 * vocabulary).
 */

/** The preset roles of v1 — module-feature-breakdown §A3. Custom roles are v1.5. */
export const PRESET_ROLES = [
  'super_admin',
  'admin',
  'manager',
  'recruiter',
  'sourcer',
  'coordinator',
  'bd',
  'read_only',
  // Partner-realm roles. Provisioned in v1, portal-gated until the portal exists.
  'client_user',
  'hiring_manager',
  'panel_member',
  // Candidate realm. Self-registered, owns only their own record.
  'candidate',
] as const;

export type RoleKey = (typeof PRESET_ROLES)[number];

/**
 * What a role may do, as verbs.
 *
 * `assign` is separate from `write` because they are separate decisions and the product says so:
 * a manager may put a recruiter on a client without being able to create or rename that client,
 * and a recruiter may do neither. Folding assignment into `write` would make those two facts one
 * permission, and the only way back would be a migration of the role table after somebody had
 * already been granted too much.
 *
 * `administer` is reserved for configuring a thing rather than using it — deleting a record,
 * changing a module's settings. Nothing in v1 grants it below `admin`.
 */
export type PolicyAction = 'read' | 'write' | 'assign' | 'administer';

/**
 * The resources v1 can be asked about.
 *
 * Enumerated so that a route naming a resource this matrix has never heard of is a build failure
 * rather than a silent deny. Deny-by-default is right at runtime and useless at review time: a
 * screen that is invisible to everyone looks exactly like a screen nobody has opened yet.
 */
export const POLICY_RESOURCES = [
  'me',
  'client',
  'client_contact',
  'contract',
  'member',
  'job',
  /**
   * The money on one requisition — change order 3 §8, respecified 11 Aug 2026.
   *
   * **A resource of its own, and it exists for exactly one reason:** a manager may set the rates on
   * a job, and may not touch the client's rate cards or its contract terms. `contract` cannot say
   * that — it is one grant covering every commercial figure in the product, so widening it for a
   * manager would hand them the account's negotiated fees at the same time, which §5.2 forbids in
   * the same table that asks for this.
   *
   * §11 of the change order flagged this as open ("Managers and commercials"); the answer of
   * 11 Aug 2026 is that the *job's* numbers are a delivery decision a team lead owns, while the
   * *client's* are an administrative one. Two resources, because they are two questions.
   *
   * Reading it stops where `read contract` stops today. The two are separate because they will
   * diverge — a tenant setting widening the job's numbers to recruiters is roadmap-parked §3.4 —
   * and because a grant that means two things is a grant nobody can change safely.
   */
  'job_commercials',
  /**
   * The most a candidate may be offered on this requisition — job module §9, split out 13 Aug 2026.
   *
   * **Split from `job_commercials`, and the split is the point.** On a C2C or C2H requisition there
   * are two figures and they belong to two different people: the **budget** is the ceiling a
   * recruiter negotiates under, and the **bill rate** is what the client is charged. The difference
   * between them is the margin, which is a commercial position and stays with a manager.
   *
   * Until today one grant carried both, so a recruiter could record neither — which meant the
   * person actually talking to candidates could not write down the number they were working to, and
   * had to ask a manager to type it for them or keep it in their head.
   *
   * Granting `write job_budget` to a recruiter hands them the ceiling and nothing behind it. They
   * still cannot see the bill rate (`read contract`), still cannot see the margin, and the offer
   * ceiling they already saw is unchanged. It is exactly the same reasoning that separated
   * `job_commercials` from `contract` one level down: a grant that means two things is a grant
   * nobody can change safely.
   */
  'job_budget',
  /**
   * Whether a requisition may be pushed outside the agency — job module requirements §5.2a.
   *
   * **Not `write job`.** A recruiter opens requisitions all day, and §5.2a's whole point is that
   * opening one is *internal* work: *"An open job is internal work. Pushing it anywhere outside —
   * social, portals, any future distribution — requires a separate 'cleared for publishing'
   * toggle, default off, set by manager and above (the same line that owns the agency's public
   * voice)."* Folding it into `write job` would mean every recruiter who may open a job may also
   * publish one, which is the opposite of what that sentence asks for.
   *
   * **Not `write job_commercials` either**, though the two grant sets happen to be identical
   * today. That coincidence is not a reason to share a key: one asks "may this person price a
   * requisition", the other "may this person speak for the agency in public", and a grant that
   * means two things is a grant nobody can change safely — the same argument `job_commercials`
   * itself makes against being folded into `contract`.
   *
   * Read is not granted separately: the state is on the job, visible to anyone who may read it,
   * because "internal only" is information a recruiter needs rather than a permission they hold.
   */
  'job_publishing',
  'candidate',
  'application',
  'interview',
  'commitment',
  'activity',
  'stage_template',

  /**
   * The agency's own words for why a requisition ended — job module requirements §5.4, D-06.
   *
   * **Not `stage_template`**, though both are "the agency's vocabulary" and the grant sets are
   * identical today. That resource is the pipeline's stage and status vocabulary; this one is a
   * clients concern living in a clients table, and the two are administered from different screens
   * by people answering different questions. `job_publishing` makes the same argument against
   * being folded into `job_commercials`: a grant that means two things is a grant nobody can
   * change safely.
   *
   * **Not `administer job` either.** That grant already means "restructure this requisition"; a
   * closure vocabulary is a tenant-wide setting that happens to be *about* jobs, and a manager who
   * may administer a job should not thereby rename `lost` for the whole agency.
   *
   * `read` is wide because every screen showing a closed requisition renders the label, and a
   * label nobody may read is a column that always displays its core value.
   */
  'closure_label',
  /**
   * An agency's own AI switches — added 13 Aug 2026 with the first live providers.
   *
   * Granted only to super_admin and admin, and granted as 'administer' rather than 'write'. That
   * distinction is the instruction this resource exists to carry: manager and recruiter hold 'write'
   * on most resources here, so 'write' would have let a recruiter switch the assistant off for the
   * whole agency — and would have taught the next person adding a route to do the same.
   *
   * Note what it does *not* govern: which model a feature runs on, where it runs, or what it costs.
   * Those are platform-plane decisions behind platform_role, and no route on this resource can
   * reach them.
   */
  'ai_settings',
  /**
   * What one candidate was told they would be paid — M-22, change order 7 §5.
   *
   * **Its own resource rather than a use of `application`**, and the argument is the one
   * `job_commercials` makes against being folded into `contract`: an offer carries a person's
   * salary, and `read application` is held by everybody who works a pipeline. Folding them would
   * hand the whole desk every candidate's CTC the day this table filled.
   *
   * **`administer` is the approval grant, and there is no fifth verb.** Approving an offer is
   * "somebody senior signed this off", which is what `administer` already means everywhere else in
   * this matrix — it is what a manager holds over `member` to remove somebody. Adding `approve` to
   * `PolicyAction` would ripple through every gate, every test and every route in the product to
   * express a distinction the existing four already carry. So `write` drafts and edits; only
   * `administer` approves, and the route additionally refuses the drafter their own approval,
   * because "a second pair of eyes" is a rule about *people* and no permission matrix can say it.
   *
   * `read_only` holds nothing here, deliberately and consistently: it holds neither `contract`,
   * `job_commercials` nor `job_budget`, so it is the one role in this file that has already been
   * decided not to see money.
   */
  'offer',
  /**
   * The agency's offer rules — its CTC vocabulary, its approval threshold, its response window.
   *
   * Granted exactly as `ai_settings` is, to `super_admin` and `admin` alone and as `administer`
   * rather than `write`, and for the reason stated there: manager and recruiter hold `write` on
   * most resources, so `write` here would let a recruiter raise the threshold above the offer
   * they are about to make and approve it themselves.
   *
   * **Not `administer offer`.** That grant means "approve this offer"; this one means "change the
   * rule every offer is approved under". A grant that means two things is a grant nobody can
   * change safely — the argument `job_publishing` and `closure_label` each make about themselves.
   */
  /**
   * How this desk weighs fit — M-48, migration 205.
   *
   * Which factors count towards a fitment score and at what weight. A resource of its own rather
   * than `application`, and the permission matrix is what found that: with `write application` a
   * **recruiter could re-weight the formula so their own candidate scored higher**, and then
   * submit them to a client with the number attached as the justification. That is the same shape
   * of self-dealing `offer_settings` exists to prevent — raising the approval threshold above the
   * offer you are about to make — and it gets the same answer.
   *
   * **`administer` rather than `write`**, on `offer_settings`' argument: manager and recruiter hold
   * `write` on most resources, so `write` here would grant exactly the thing the paragraph above
   * refuses.
   *
   * `read` is granted alongside it rather than to everybody, because nothing but the settings
   * screen needs it: a breakdown reaches the drawer on `pipeline.application.read`, which every
   * role may call, and it carries the weights that were used already. Reading the *current* formula
   * is an administrator's question about configuration, not a recruiter's about a candidate.
   */
  'match_settings',
  'offer_settings',
  /**
   * The agency's own display conventions — M-47, migration 200.
   *
   * Today that is one field, the order a date is written in. It is a resource rather than a column
   * on something else because of who may change it: everybody has to *read* it — a screen cannot
   * render a date without knowing the order — while changing it reformats every date every
   * colleague sees, which is an administrator's decision in the same way the approval threshold is.
   *
   * **`read` is not granted here at all, and that is deliberate.** `access.me.read` already hands
   * the tenant's conventions to every authenticated principal alongside its locale and currency,
   * under `read me`. Minting a second grant for the same bytes would mean a role could hold one and
   * not the other, and a screen would have to decide which absence to believe.
   *
   * **`administer` rather than `write`**, on `offer_settings`' argument: manager and recruiter hold
   * `write` on most resources, and this is a setting rather than a record.
   */
  'agency_profile',
  /**
   * Revealing a candidate's sealed PAN or Aadhaar — M-43, migration 197.
   *
   * A resource rather than a fifth verb, for the reason `PolicyAction` argues at length: four verbs
   * are enough if the *thing* is named precisely, and a fifth would have to be granted everywhere
   * before it could be withheld anywhere.
   *
   * **Not `read candidate`.** Anybody working a pursuit can see a candidate, and they can see the
   * masked form — `XXXXX1234F` is enough to agree it is the same card. Seeing the number itself is
   * a different act with a different blast radius, it writes an audit row that names the person who
   * did it, and it is the one thing on this record that a leak makes unrecoverable: a candidate can
   * change a phone number and cannot change their Aadhaar.
   *
   * So: `administer` only, and only the two roles that already carry the agency's own liabilities.
   * A recruiter holding `read`/`write candidate` gets the mask and no toggle, which is the correct
   * default for the person who will handle a hundred of these a week.
   */
  'candidate_identity',
  /**
   * The mailbox this agency sends through — M-16, change order 7 §6.
   *
   * Granted exactly as `ai_settings` and `offer_settings` are: `super_admin` and `admin` alone,
   * and `administer` rather than `write`. The instruction of 09 Aug 2026 about AI providers reads
   * across word for word — *"only admins can add, enable… all other roles have no say in this
   * configuration"* — and it is stronger here, because what is being configured is a credential to
   * somebody's mail server rather than a preference.
   *
   * **`read` is not wider either, and that is a departure worth naming.** A recruiter whose
   * interview invitation did not send is told so by the screen that failed, with a sentence naming
   * the settings page and the person who can open it. Widening `read` would put a page listing the
   * agency's mail hosts and usernames in front of the whole desk to save an administrator one
   * message.
   */
  'email_sender',
  /**
   * The mail this agency has sent, and whether it arrived — M-16, 27 Aug 2026.
   *
   * **Admin and above, on `email_sender`'s reasoning rather than by copying it.** That resource
   * argues its own `read` narrow because the screen lists mail hosts and usernames. This one lists
   * something different and no less sensitive: **every address the agency has written to** — every
   * candidate, every colleague, every client contact, with the subject line beside it. That is a
   * screen for the person answering *"they say they never got it"*, not one for the whole desk.
   *
   * That comment on `email_sender` — *"a recruiter whose interview invitation did not send is told
   * so by the screen that failed"* — describes a screen that does not exist yet. It stays true as
   * an intention: the fix for a recruiter's visibility is a message on the interview itself, not a
   * list of everybody's correspondence.
   *
   * `write` is the retry, and it is deliberately the same audience rather than narrower. Somebody
   * who can see that an invitation failed and cannot re-send it has been shown a problem and denied
   * the fix, which is worse than not showing them.
   */
  'message',
  /**
   * Which messages this agency sends at all — M-16 epic C5, 28 Aug 2026.
   *
   * A separate resource from `message` rather than a third verb on it, because the two answer
   * different questions about different objects. `message` is *correspondence* — what was said to
   * whom, and the retry. This is *configuration*: a standing decision that changes what happens to
   * every future candidate, colleague and client contact, with no row anywhere to review it
   * against. Switching the interview invitation off is closer to changing the mailbox than it is to
   * re-sending one message, and `email_sender` is the audience it belongs beside.
   *
   * `administer` rather than `write`, for the same reason `email_sender` uses it: this is not an
   * edit to a record, it is a setting the whole agency then lives under.
   */
  'message_setting',
  /**
   * Writing to a candidate on a candidature — 30 Sep 2026, at the product owner's instruction.
   *
   * ── Why this is not a verb on `message` ───────────────────────────────────────────────────────
   *
   * `message` is the agency's whole correspondence log, and it argues its own audience narrow for a
   * reason that still holds: it lists *"every address the agency has written to — every candidate,
   * every colleague, every client contact, with the subject line beside it"*. That is a screen for
   * the person answering "they say they never got it", and it should stay admin-only.
   *
   * The correspondence slice landed on that resource anyway, because `message` was the only thing
   * that looked like correspondence — and inherited admin-only with it. So *"writing moves to the
   * application"* shipped with the one role it was built for unable to use it, which the permission
   * matrix made visible the first time it was asked to enrol the routes.
   *
   * Widening `message` would have fixed the screen and put the agency's entire mail log in front of
   * the whole desk to do it. This is the narrower fact: **one message, to one candidate, on one
   * candidature you are working.** Different object, different blast radius, different audience.
   *
   * ── The instruction, and the half of it this does not yet do ──────────────────────────────────
   *
   * *"A recruiter can write to a candidate under a Job. Anyone assigned to that job should be able
   * to do the communication."*
   *
   * The roles below are the first half. The second — **assigned to that job** — is record scope,
   * not a role, and `outreach` cannot answer it today: assignment is `workspace.job_assignment`,
   * which belongs to another module, and MB-02 forbids reaching for it. It needs the fact to travel
   * on an event into a projection this module owns, which is a slice rather than a line. Recorded
   * in build-status rather than approximated here, because a grant that says "assigned" and checks
   * only the role is a control that reports a state it does not enforce.
   *
   * `read` is the same message on the candidature's own timeline — the person who may write to
   * somebody may see what was written to them.
   */
  'candidate_correspondence',
  /**
   * The meeting account this agency creates joining links on — M-17, 1 Sep 2026.
   *
   * **Three verbs, and the third is what makes one resource enough for two tiers.** A meeting
   * connection exists at two levels — the agency's, connected once by an administrator and used by
   * everybody, and a recruiter's own, so their meetings are created on their account — and the
   * question *"may you do this"* has a different answer for each:
   *
   *   `read`        what is connected and whether it works. Everybody who schedules a round, because
   *                 *"there is no link and here is why"* is a sentence they meet at the composer.
   *   `write`       connect and disconnect **your own** account, in your own profile. The same
   *                 audience: somebody who cannot book an interview has no meetings to create.
   *   `administer`  the **agency's** connection, which every colleague without one of their own then
   *                 mints on. Super admin and admin alone.
   *
   * The split is the same one `offer` draws between editing an offer and administering the rule
   * every offer is approved under — and it is what stops the alternative, which was two resources
   * differing only in scope and therefore two places to keep one rule.
   *
   * **`read` deliberately does not follow `email_sender`'s narrow reading.** That resource argues
   * itself down to administrators because its screen lists mail hosts, usernames and the shape of
   * somebody's SMTP setup. This one lists an account name and a status, and it is read at the moment
   * a recruiter is trying to schedule something. Withholding it would put "create the link" in front
   * of somebody and no way to find out why it refuses.
   *
   * The credential is on neither list, at any verb. No route returns it — see migration 158.
   */
  'meeting_connection',
] as const;

export type PolicyResource = (typeof POLICY_RESOURCES)[number];

export interface PolicyQuestion {
  readonly roleKey: string;
  readonly action: string;
  readonly resource: string;
}

export interface PolicyDecision {
  readonly allowed: boolean;
  /** Present when refused. Reaches the client, so it names the rule and never the data. */
  readonly reason?: string;
}

/**
 * The v1 permission matrix: role → resource → actions.
 *
 * Written out per resource rather than as a role-wide verb list, because the product's rules are
 * per resource and a role-wide list cannot express them. Under the old shape `recruiter` held
 * `write`, which meant a recruiter could create a client — the same permission that let them
 * update one. Those are different decisions and this table now says so.
 *
 * What the rules are, in the business's words:
 *
 *   client   Everyone in the tenant can see the client list; that is the shared context the work
 *            happens in. The verbs then split three ways, and change order 3 §5.2 is what forced
 *            the split:
 *
 *              `administer`  create a client. A commercial relationship is signed, not opened in
 *                            the course of a day's work, so this stays with the **admin**.
 *              `write`       change the client's own state — today, its status. §5.2 grants this
 *                            to a **manager**, who runs the desk and is the person who knows a
 *                            prospect has gone quiet.
 *              `assign`      decide who works on it and who owns the relationship. Already the
 *                            manager's, and §6's owner change is the same kind of decision.
 *
 *            This is a deliberate move of `create` from `write` to `administer`. The alternative
 *            was to grant a manager `write` and accept that they could also create clients, which
 *            contradicts a rule this matrix has stated since it was written. Splitting the verb
 *            keeps both facts true at once.
 *   client_contact
 *            The **delivery** contacts — the people needed to run a job. Change order 3 §1.3: a
 *            recruiter meets a new panellist in the course of the work and must be able to add
 *            them on the spot, because "blocking this only produces private spreadsheets".
 *
 *            Its own resource rather than a use of `client`, because the two answer differently:
 *            a recruiter may add a hiring manager and may not touch the client record. Everyone
 *            who can see a client can see its delivery contacts; **recruiter and above** add and
 *            edit them; removal is `administer` and stops at **manager**.
 *
 *            The other family — HR, finance, accounts, signatory — is not here. Those are the
 *            commercial counterparts, they live behind `contract`, and its existing grants already
 *            say exactly what §5.2 asks: read stops at manager, write stops at admin. Reusing it
 *            is not a shortcut; an engagement contact *is* part of the agreement, and a product
 *            that let somebody read the fee but not the finance contact would be drawing a line
 *            nobody asked for.
 *   contract §2's `view_commercials` capability, expressed as the thing this model already has: a
 *            resource nobody else holds. §15 is explicit that commercial visibility is role-gated
 *            — "a sourcer/junior may see the client but not contract terms" — and the alternative
 *            was a parallel capability system beside this table, which would give the product two
 *            answers to "may they?" and leave the second untested.
 *
 *            The grant stops at **manager**, one rung tighter than §15's own example, and that is
 *            the one judgement call here. `fee_value` is the agency's margin on a placement, and a
 *            recruiter negotiating a salary with a candidate is on the other side of that number.
 *            The counter-argument is real: `guarantee_period_days` and `candidate_ownership_days`
 *            are operational and a recruiter has a legitimate use for both. If that wins, the fix
 *            is to split the *response* — operational fields for everyone, money for this list —
 *            rather than to widen the grant, because widening it hands over the margin too.
 *
 *            `bd` reads and does not write: business development negotiates terms and does not
 *            commit them, which is the line `client.create` already draws.
 *   member   Everyone can see who is on the team. Only an **admin** invites people, changes a
 *            role, or deactivates an account — a recruiter who could grant roles could grant
 *            themselves one.
 *   job      Everyone can see the job board. A **recruiter and above** opens a requisition and
 *            changes its status — a job is daily operational work, and the spec's scope model
 *            ("recruiter own/group", §6.1) only means something if a recruiter can own one.
 *            Sourcers and coordinators read: they work a pipeline, they do not create the order.
 *            This is the deliberate difference from `client`, where creation is an admin act
 *            because a client is a commercial relationship rather than a day's work.
 *   candidate Everyone in the tenant can see and add candidates — sourcing is what a sourcer is
 *            *for*, and a coordinator scheduling interviews needs the person's record. This is
 *            the widest write in the matrix and deliberately so: the scarce thing in an agency is
 *            people in the database, and a permission that slows that down costs more than it
 *            protects. `administer` stays with the admin, because that is where deletion and
 *            do-not-contact reversal will land.
 *   me       Every role reads its own session. Gating this would make a misconfigured principal
 *            unable to discover that they are misconfigured.
 *   application
 *            Everyone in the tenant reads the board — a pipeline nobody can see is a pipeline
 *            nobody works. Writing stops at **recruiter**, one rung tighter than `candidate`, and
 *            the reason is that `write` on this resource currently spans two acts that ought to
 *            differ: putting somebody into a pipeline at `sourced`, and moving them to `joined`,
 *            which decides who the placement fee is attributed to. A sourcer should plainly have
 *            the first and plainly not the second. Until a step-level permission exists to say
 *            that, the narrower grant is the safer default — widening a permission later is a
 *            settings change, and narrowing one is a conversation with somebody who had it.
 *   interview
 *            Its own resource rather than a use of `application`, and this is the one place the
 *            matrix widens rather than narrows. Spec §12 calls the tracker "coordination for
 *            recruiters/**coordinators**" with inline status update — and a coordinator holds
 *            `read` on `application` for the reason set out above: `write application` currently
 *            spans adding somebody at `sourced` and marking them `joined`, and a coordinator
 *            should have neither.
 *
 *            Scheduling a round and recording how it went is neither of those acts. It moves
 *            nothing, decides nobody's fee, and is precisely the work a coordinator is employed
 *            to do. Borrowing `write application` for it would have forced a choice between
 *            giving coordinators the placement decision and giving them a tracker they cannot
 *            use — so the verb gets its own noun. Sourcers and BD read; `read_only` reads.
 *            `administer` is unheld: there is no configure-an-interview act, and the plan that
 *            comes closest is governed by `stage_template` and `job` instead.
 *   commitment
 *            §7's standup. Everyone in the tenant reads and writes it, including `read_only` on
 *            the read side — and the write is wider than any other in this matrix on purpose.
 *            §7.2: "**anyone in the group adds** (shared); manager facilitates". A standup where
 *            only a manager may type is a standup one person runs and nobody owns, which is the
 *            failure mode the shared board exists to avoid. The thing that keeps it honest is not
 *            a permission — it is that the *number* beside a commitment cannot be typed at all.
 *            `sourcer` and `coordinator` hold write for the same reason: they make promises about
 *            their day like everybody else.
 *   activity
 *            The timeline (§28). Read-only for every role and writable by none, because nothing
 *            writes it: every row is projected from an event. A `write` grant would be a
 *            capability with no route behind it, which is how a permission quietly becomes true.
 *   stage_template
 *            Everyone reads it, because the labels *are* the board's columns. Only an **admin**
 *            `administer`s it: this is the vocabulary every recruiter in the agency then works
 *            inside, and renaming a stage mid-quarter changes what every report means. Nobody
 *            holds `write` — there is no partial edit of a template in v1, only setting the
 *            agency up with the standard stages.
 *
 * An omitted resource means no permission at all, and the enrolment test below turns that into a
 * failing build for any route whose resource is missing here. Partner and candidate roles hold
 * nothing on these resources: their surfaces are the portals, which do not exist yet, and giving
 * them provisional access now is how a portal-gated role quietly becomes an ungated one.
 */
const PERMISSIONS: Readonly<
  Record<RoleKey, Partial<Record<PolicyResource, readonly PolicyAction[]>>>
> = {
  super_admin: {
    me: ['read'],
    client: ['read', 'write', 'assign', 'administer'],
    client_contact: ['read', 'write', 'administer'],
    contract: ['read', 'write', 'administer'],
    /** Every commercial figure, a job's included. See `job_commercials` in POLICY_RESOURCES. */
    job_commercials: ['read', 'write'],
    job_budget: ['read', 'write'],
    /** §5.2a's readiness toggle — manager and above, the line that owns the agency's public voice. */
    job_publishing: ['write'],
    member: ['read', 'write', 'assign', 'administer'],
    job: ['read', 'write', 'assign', 'administer'],
    candidate: ['read', 'write', 'assign', 'administer'],
    application: ['read', 'write', 'assign', 'administer'],
    interview: ['read', 'write', 'assign', 'administer'],
    commitment: ['read', 'write', 'assign', 'administer'],
    activity: ['read'],
    stage_template: ['read', 'administer'],
    /** §5.4's vocabulary. Administered with the rest of the agency's settings. */
    closure_label: ['read', 'administer'],
    /**
     * The agency's AI switches. Read and administer, and this pair appears on no other role.
     *
     * Read is deliberately not wider: a recruiter meeting a missing assistant should be told by
     * the screen it is missing from, not by a Settings page they cannot open. That is a UI
     * decision the /v1/me surface will carry when it does, and not a reason to widen a grant.
     */
    ai_settings: ['read', 'administer'],
    /** Draft, edit and approve. See `offer` in POLICY_RESOURCES on why `administer` is approval. */
    offer: ['read', 'write', 'administer'],
    match_settings: ['read', 'administer'],
    offer_settings: ['read', 'administer'],
    /** The house date order. See `agency_profile` in POLICY_RESOURCES. */
    agency_profile: ['administer'],
    /** The reveal, and its log. See `candidate_identity` in POLICY_RESOURCES. */
    candidate_identity: ['administer'],
    /** The agency mailbox. Admin-only in both verbs — see the resource for why `read` too. */
    email_sender: ['read', 'administer'],
    /** What was sent and what became of it, and the retry. See `message` in POLICY_RESOURCES. */
    message: ['read', 'write'],
    /** And writing to one candidate on one candidature. See `candidate_correspondence`. */
    candidate_correspondence: ['read', 'write'],
    /** Which messages go out at all — beside `email_sender`, which is the settings it sits with. */
    message_setting: ['read', 'administer'],
    /**
     * The agency's meeting account, and their own. All three verbs — see `meeting_connection` in
     * POLICY_RESOURCES: `administer` is the connection the whole desk mints on, `write` is the one
     * in this person's own profile, and both belong to an administrator as well as to everybody
     * else who schedules.
     */
    meeting_connection: ['read', 'write', 'administer'],
  },
  admin: {
    me: ['read'],
    client: ['read', 'write', 'assign', 'administer'],
    client_contact: ['read', 'write', 'administer'],
    contract: ['read', 'write', 'administer'],
    /** Every commercial figure, a job's included. See `job_commercials` in POLICY_RESOURCES. */
    job_commercials: ['read', 'write'],
    job_budget: ['read', 'write'],
    /** §5.2a's readiness toggle — manager and above, the line that owns the agency's public voice. */
    job_publishing: ['write'],
    member: ['read', 'write', 'assign', 'administer'],
    job: ['read', 'write', 'assign', 'administer'],
    candidate: ['read', 'write', 'assign', 'administer'],
    application: ['read', 'write', 'assign', 'administer'],
    interview: ['read', 'write', 'assign', 'administer'],
    commitment: ['read', 'write', 'assign', 'administer'],
    activity: ['read'],
    stage_template: ['read', 'administer'],
    /** §5.4's vocabulary. Administered with the rest of the agency's settings. */
    closure_label: ['read', 'administer'],
    /**
     * The agency's AI switches. Read and administer, and this pair appears on no other role.
     *
     * Read is deliberately not wider: a recruiter meeting a missing assistant should be told by
     * the screen it is missing from, not by a Settings page they cannot open. That is a UI
     * decision the /v1/me surface will carry when it does, and not a reason to widen a grant.
     */
    ai_settings: ['read', 'administer'],
    /** Draft, edit and approve. See `offer` in POLICY_RESOURCES on why `administer` is approval. */
    offer: ['read', 'write', 'administer'],
    match_settings: ['read', 'administer'],
    offer_settings: ['read', 'administer'],
    /** The house date order. See `agency_profile` in POLICY_RESOURCES. */
    agency_profile: ['administer'],
    /** The reveal, and its log. See `candidate_identity` in POLICY_RESOURCES. */
    candidate_identity: ['administer'],
    /** The agency mailbox. Admin-only in both verbs — see the resource for why `read` too. */
    email_sender: ['read', 'administer'],
    /** The same pair as super_admin. See `message` in POLICY_RESOURCES on why not wider. */
    message: ['read', 'write'],
    candidate_correspondence: ['read', 'write'],
    message_setting: ['read', 'administer'],
    /**
     * The agency's meeting account, and their own. All three verbs — see `meeting_connection` in
     * POLICY_RESOURCES: `administer` is the connection the whole desk mints on, `write` is the one
     * in this person's own profile, and both belong to an administrator as well as to everybody
     * else who schedules.
     */
    meeting_connection: ['read', 'write', 'administer'],
  },
  manager: {
    me: ['read'],
    // `write` but not `administer`: a manager changes a client's status (change order 3 §5.2) and
    // does not create one. That distinction is why `create` moved to `administer` — see the note
    // on `client` above. `assign` was always here and now also carries the relationship owner.
    client: ['read', 'write', 'assign'],
    // Removal is `administer` and a manager holds it, but only over this resource — the route is
    // the delivery family, so §5.2's "remove a contact: manager, delivery only" is a property of
    // which routes exist rather than a condition inside one.
    client_contact: ['read', 'write', 'administer'],
    contract: ['read'],
    /**
     * **Write, where `contract` is read-only** — and this is the whole reason the resource exists.
     *
     * A team lead prices the requisitions their desk runs; the account's negotiated fees stay an
     * administrator's. §5.2's "create or edit contract terms and rate cards" row is untouched, and
     * §11's open question ("Managers and commercials") is closed the narrow way: the *job's*
     * numbers are a delivery decision, the *client's* are an administrative one.
     */
    job_commercials: ['read', 'write'],
    job_budget: ['read', 'write'],
    /** §5.2a's readiness toggle — manager and above, the line that owns the agency's public voice. */
    job_publishing: ['write'],
    member: ['read'],
    // A manager runs the desk: they open requisitions and hand them to recruiters.
    job: ['read', 'write', 'assign'],
    candidate: ['read', 'write', 'assign'],
    // Reassigning a pursuit is the same staffing decision as reassigning a job, so `assign`
    // travels with `job` rather than with `write`.
    application: ['read', 'write', 'assign'],
    // Writes to candidates on the candidatures they run. See `candidate_correspondence`.
    candidate_correspondence: ['read', 'write'],
    interview: ['read', 'write', 'assign'],
    commitment: ['read', 'write', 'assign'],
    activity: ['read'],
    stage_template: ['read'],
    closure_label: ['read'],
    /**
     * The approval grant. A manager already owns the job's numbers (`job_commercials`), so the
     * offer made under them is the same commercial position one step later.
     */
    offer: ['read', 'write', 'administer'],
    /**
     * Read and write, and no `administer` — see `meeting_connection` in POLICY_RESOURCES.
     *
     * `write` is **this person's own** meeting account, connected from their own profile so their
     * rounds are created on it. The agency's connection stays an administrator's, which is the whole
     * reason the resource carries a third verb rather than being two resources.
     */
    meeting_connection: ['read', 'write'],
  },
  recruiter: {
    me: ['read'],
    client: ['read'],
    // §1.3, in one grant: add a panellist on the spot, do not remove anybody. Removal is
    // `administer` and stops at the manager.
    client_contact: ['read', 'write'],
    member: ['read'],
    // Write but not assign. A recruiter opens a job and owns it; moving one to somebody else is
    // a staffing decision, which is the manager's.
    job: ['read', 'write'],
    /*
     * §9's budget, and this is the grant the split exists for — 13 Aug 2026.
     *
     * A recruiter negotiating with a C2C candidate works to a ceiling, and until now could not
     * write that ceiling down: one grant covered both the budget and the bill rate, so the figure
     * the recruiter *uses* was locked behind the figure they may not *see*. They still cannot see
     * the bill rate, and still cannot see the margin between the two.
     */
    job_budget: ['read', 'write'],
    candidate: ['read', 'write'],
    application: ['read', 'write'],
    /*
     * The role the correspondence slice was built for, and the one it shipped without.
     *
     * *"Writing moves to the application"* exists so the person working the candidature stops
     * using their personal mailbox, where nothing is recorded. That person is a recruiter.
     */
    candidate_correspondence: ['read', 'write'],
    interview: ['read', 'write'],
    commitment: ['read', 'write'],
    activity: ['read'],
    stage_template: ['read'],
    closure_label: ['read'],
    /** Drafts and edits; `administer` is withheld, so nobody can approve their own offer. */
    offer: ['read', 'write'],
    /**
     * Read and write, and no `administer` — see `meeting_connection` in POLICY_RESOURCES.
     *
     * `write` is **this person's own** meeting account, connected from their own profile so their
     * rounds are created on it. The agency's connection stays an administrator's, which is the whole
     * reason the resource carries a third verb rather than being two resources.
     */
    meeting_connection: ['read', 'write'],
  },
  sourcer: {
    me: ['read'],
    client: ['read'],
    client_contact: ['read'],
    member: ['read'],
    job: ['read'],
    // A sourcer's whole job is putting people in the database.
    candidate: ['read', 'write'],
    // Read only, and reluctantly — see the `application` note above. Adding somebody to a
    // pipeline is sourcing and ought to be here; marking one `joined` is not, and today they are
    // the same verb.
    application: ['read'],
    interview: ['read'],
    commitment: ['read', 'write'],
    activity: ['read'],
    stage_template: ['read'],
    closure_label: ['read'],
  },
  coordinator: {
    me: ['read'],
    client: ['read'],
    client_contact: ['read'],
    member: ['read'],
    job: ['read'],
    candidate: ['read', 'write'],
    application: ['read'],
    // §12's whole point: a coordinator runs the interview tracker.
    interview: ['read', 'write'],
    /*
     * A coordinator already writes to candidates — `pipeline.interview.send_invitation` is theirs,
     * and rescheduling a round is a conversation. Withholding the plain message would leave them
     * sending the reschedule from their own mailbox, which is the thing this is for.
     */
    candidate_correspondence: ['read', 'write'],
    commitment: ['read', 'write'],
    activity: ['read'],
    stage_template: ['read'],
    closure_label: ['read'],
    /**
     * Read and write, and no `administer` — see `meeting_connection` in POLICY_RESOURCES.
     *
     * `write` is **this person's own** meeting account, connected from their own profile so their
     * rounds are created on it. The agency's connection stays an administrator's, which is the whole
     * reason the resource carries a third verb rather than being two resources.
     */
    meeting_connection: ['read', 'write'],
  },
  bd: {
    me: ['read'],
    // Business development converts leads into clients (§B2), which is a V1.5 flow. Until it
    // exists, adding a client stays with the admin — the rule the product asked for.
    client: ['read'],
    client_contact: ['read'],
    contract: ['read'],
    // Reads the numbers it negotiated and commits none of them, on the job exactly as on the
    // client — the same line `contract` draws for this role.
    job_commercials: ['read'],
    job_budget: ['read'],
    member: ['read'],
    job: ['read'],
    candidate: ['read'],
    application: ['read'],
    interview: ['read'],
    commitment: ['read'],
    activity: ['read'],
    stage_template: ['read'],
    closure_label: ['read'],
  },
  read_only: {
    me: ['read'],
    client: ['read'],
    client_contact: ['read'],
    member: ['read'],
    job: ['read'],
    candidate: ['read'],
    application: ['read'],
    interview: ['read'],
    commitment: ['read'],
    activity: ['read'],
    stage_template: ['read'],
    closure_label: ['read'],
  },
  // Partner realm. These principals never reach a workforce route — the realm check refuses them
  // first — so this is defence in depth rather than the enforcement.
  client_user: { me: ['read'] },
  hiring_manager: { me: ['read'] },
  panel_member: { me: ['read'] },
  // Candidate realm, same reasoning.
  candidate: { me: ['read'] },
};

/** What a role may do to one resource. Empty for an unknown role or an ungranted resource. */
export function actionsFor(roleKey: string, resource: string): readonly PolicyAction[] {
  const forRole = PERMISSIONS[roleKey as RoleKey];
  if (!forRole) {
    return [];
  }
  return forRole[resource as PolicyResource] ?? [];
}

/** Every resource a role can touch at all. Used by the UI to decide what to offer. */
export function resourcesFor(roleKey: string): readonly PolicyResource[] {
  const forRole = PERMISSIONS[roleKey as RoleKey];
  return forRole ? (Object.keys(forRole) as PolicyResource[]) : [];
}

/**
 * Whether the action is one this role holds on this resource.
 *
 * An unknown role gets nothing, and so does a known role on an unlisted resource. That is the
 * important half: a typo in a role key, a role removed from the table but still on a membership
 * row, or a resource nobody thought about all deny rather than default. Failing open here would
 * mean a deleted role silently keeping its permissions.
 */
export function isActionPermitted(question: PolicyQuestion): PolicyDecision {
  const permitted = actionsFor(question.roleKey, question.resource);

  if (permitted.length === 0) {
    return {
      allowed: false,
      // Names the rule, never the data — this reason reaches the client.
      reason: `Role "${question.roleKey}" holds no permissions on ${question.resource}.`,
    };
  }

  if (!permitted.includes(question.action as PolicyAction)) {
    return {
      allowed: false,
      reason: `Role "${question.roleKey}" may not ${question.action} ${question.resource}.`,
    };
  }

  return { allowed: true };
}
